import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Passenger } from '@models/booking.models';
import {
  ApisAddressStatus,
  DocumentTypeCode,
  PassengerAssistanceUpdateRequest,
  PassengerInfant,
  PassengerTravelDocument,
  PassengerUpdateRequest,
} from '@models/requests/passenger-update-request.model';
import { ApiResponse } from '@models/responses/api-response.model';
import { EnvironmentService } from '../environment-service/environment.service';
import {
  PassengerForm,
  TripDetailsPassengerForm,
} from '@models/form/form.models';
import { DateTime } from 'luxon';
import {
  DATE_FORMAT_DEFAULT,
  DATE_FORMAT_SHORT_DATE_MONTH_YEAR,
} from '@constants/date-format.constants';
import { createPhoneNumberFromParts } from '@utils/data-utils';
import { CountryPhoneCode } from '@models/cms/app.model';
import { NullableString } from '@models/app.models';
import { FALLBACK_CULTURE } from '@constants/app.constants';
@Injectable({
  providedIn: 'root',
})
export class PassengerService {
  private static readonly CONTROLLER_ROOT = 'v1/passengers';

  constructor(private httpClient: HttpClient) {}

  updatePassengersInformation(
    passengers: PassengerUpdateRequest[],
  ): Observable<ApiResponse<null>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${PassengerService.CONTROLLER_ROOT}`;
    return this.httpClient.put<ApiResponse<null>>(endpoint, passengers);
  }

  updatePassengersAssistance(
    passengerAssistance: PassengerAssistanceUpdateRequest[],
  ): Observable<ApiResponse<null>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${PassengerService.CONTROLLER_ROOT}/assistance`;
    return this.httpClient.post<ApiResponse<null>>(
      endpoint,
      passengerAssistance,
    );
  }

  transformTripDetailsFormDataToPassengerUpdateRequest(
    passenger: Passenger,
    form: TripDetailsPassengerForm,
    phoneCodes: CountryPhoneCode[],
    culture?: NullableString,
  ): PassengerUpdateRequest {
    const phone = this.getPhoneNumberFromForm(form, phoneCodes);
    const apisAddress = passenger.addresses?.find(
      (addr) => addr.status === ApisAddressStatus.InCountry,
    );
    const contactAddress = passenger.addresses?.find(
      (addr) => addr.status === ApisAddressStatus.Contact,
    );
    // Currently infants will only have 1 travel document.
    const infant =
      passenger.infant &&
      ({
        ...passenger.infant,
        nationality: form.infantPassportNationality ? form.infantPassportNationality : passenger.infant.nationality,
        travelDocuments: form.infantPassportNumber
          ? [
              {
                documentTypeCode: DocumentTypeCode.Passport,
                expirationDate: form.infantPassportExpirationDate
                  ? DateTime.fromFormat(
                      form.infantPassportExpirationDate,
                      DATE_FORMAT_DEFAULT,
                      {
                        locale: culture ?? FALLBACK_CULTURE,
                      },
                    ).toISODate()
                  : undefined,
                issuedDate: form.infantPassportIssuedDate
                  ? DateTime.fromFormat(
                      form.infantPassportIssuedDate,
                      DATE_FORMAT_DEFAULT,
                      {
                        locale: culture ?? FALLBACK_CULTURE,
                      },
                    ).toISODate()
                  : undefined,
                issuedByCode: form.infantPassportCountryIssuedBy,
                number: form.infantPassportNumber,
                birthCountry: form.infantPassportBirthCountry,
                nationality: form.infantPassportNationality,
              } as PassengerTravelDocument,
            ] as PassengerTravelDocument[]
          : null,
      } as PassengerInfant);

    return {
      passengerKey: passenger.passengerKey,
      passengerTypeCode: passenger.passengerTypeCode,
      passengerAlternateKey: passenger.passengerAlternateKey,
      infant: infant,
      name: {
        first: form.firstName,
        middle: form.middleName || undefined,
        last: form.lastName,
        suffix: form.suffix || undefined,
      },
      dateOfBirth: passenger.info.dateOfBirth
        ? passenger.info.dateOfBirth
        : form.birthDay && form.birthMonth && form.birthYear
        ? DateTime.fromFormat(
            `${form.birthDay}-${form.birthMonth}-${form.birthYear}`,
            DATE_FORMAT_SHORT_DATE_MONTH_YEAR,
            {
              locale: culture ?? FALLBACK_CULTURE,
            },
          ).toISODate()
        : null,
      // these things can't be null by the time they get to the trip summary
      gender: passenger.info.gender,
      // contact
      emailAddress: form.email,
      phone: phone,
      // travel numbers
      redressNumber: form.redressNumber,
      knownTravelerNumber: form.knownTravelerNumber,
      // passport
      issuedByCode: form.passportCountryIssuedBy,
      birthCountry: form.passportBirthCountry,
      nationality: form.passportNationality,
      passportExpirationDate: form.passportExpirationDate
        ? DateTime.fromFormat(
            form.passportExpirationDate,
            DATE_FORMAT_DEFAULT,
            {
              locale: culture ?? FALLBACK_CULTURE,
            },
          ).toISODate()
        : undefined,
      passportIssuedDate: form.passportIssuedDate
        ? DateTime.fromFormat(form.passportIssuedDate, DATE_FORMAT_DEFAULT, {
            locale: culture ?? FALLBACK_CULTURE,
          }).toISODate()
        : undefined,
      passportNumber: form.passportNumber,
      passengerAddressKey: contactAddress?.passengerAddressKey,
      apisAddress: {
        passengerAddressKey: apisAddress?.passengerAddressKey,
        destinationStationCode: apisAddress?.stationCode,
        addressLine1: form.addressLine1,
        city: form.city,
        provinceState: form.provinceState,
        postalCode: form.postalCode,
        countryCode: form.country,
      },
      residentCountry: form.residentCountry,
      residentPostalCode: form.residentPostalCode,
      residentProvinceState: form.residentProvinceState,
    };
  }

  transformFormDataToPassengerUpdateRequest(
    passenger: Passenger,
    phoneCodes: CountryPhoneCode[],
    form: PassengerForm,
    culture?: NullableString,
  ): PassengerUpdateRequest {
    const phone = this.getPhoneNumberFromForm(form, phoneCodes);

    let infant: PassengerInfant | undefined = undefined;
    if (
      form.infantFirstName &&
      form.infantLastName &&
      form.infantGender &&
      form.infantBirthDay &&
      form.infantBirthMonth &&
      form.infantBirthYear
    ) {
      infant = {
        name: {
          first: form.infantFirstName,
          middle: form.infantMiddleName || undefined,
          last: form.infantLastName,
          suffix: form.infantSuffix || undefined,
        },
        dateOfBirth: DateTime.fromFormat(
          `${form.infantBirthDay}-${form.infantBirthMonth}-${form.infantBirthYear}`,
          DATE_FORMAT_SHORT_DATE_MONTH_YEAR,
          {
            locale: culture ?? FALLBACK_CULTURE,
          },
        ).toISODate(),
        gender: Number(form.infantGender),
      };
    }

    const apisAddress = passenger.addresses?.find(
      (addr) => addr.status === ApisAddressStatus.InCountry,
    );
    const contactAddress = passenger.addresses?.find(
      (addr) => addr.status === ApisAddressStatus.Contact,
    );
    return {
      passengerKey: passenger.passengerKey,
      passengerTypeCode: passenger.passengerTypeCode,
      passengerAlternateKey: passenger.passengerAlternateKey,
      infant,
      name: {
        first: form.firstName,
        middle: form.middleName || undefined,
        last: form.lastName,
        suffix: form.suffix || undefined,
      },
      dateOfBirth:
        form.birthDay && form.birthMonth && form.birthYear
          ? DateTime.fromFormat(
              `${form.birthDay}-${form.birthMonth}-${form.birthYear}`,
              DATE_FORMAT_SHORT_DATE_MONTH_YEAR,
              {
                locale: culture ?? FALLBACK_CULTURE,
              },
            ).toISODate()
          : undefined,
      gender: Number(form.gender),
      // contact
      emailAddress: form.email,
      phone,
      residentCountry: form.residentCountry,
      residentPostalCode: form.residentPostalCode,
      residentProvinceState: form.residentProvinceState,
      // travel numbers
      redressNumber: form.redressNumber,
      knownTravelerNumber: form.knownTravelerNumber,
      // passport
      issuedByCode: form.passportCountryIssuedBy,
      birthCountry: form.passportBirthCountry,
      nationality: form.passportNationality,
      passportExpirationDate: form.passportExpirationDate
        ? DateTime.fromFormat(
            form.passportExpirationDate,
            DATE_FORMAT_DEFAULT,
            {
              locale: culture ?? FALLBACK_CULTURE,
            },
          ).toISODate()
        : undefined,
      passportIssuedDate: form.passportIssuedDate
        ? DateTime.fromFormat(form.passportIssuedDate, DATE_FORMAT_DEFAULT, {
            locale: culture ?? FALLBACK_CULTURE,
          }).toISODate()
        : undefined,
      passportNumber: form.passportNumber,
      passengerAddressKey: contactAddress?.passengerAddressKey,
      apisAddress: {
        passengerAddressKey: apisAddress?.passengerAddressKey,
        destinationStationCode: apisAddress?.stationCode,
        addressLine1: apisAddress?.lineOne,
        city: apisAddress?.city,
        provinceState: apisAddress?.provinceState,
        postalCode: apisAddress?.postalCode,
        countryCode: apisAddress?.countryCode,
      },
    };
  }

  transformFormDataToPassengerAssistanceUpdateRequest(
    passenger: Passenger,
    form: PassengerForm | TripDetailsPassengerForm,
  ): PassengerAssistanceUpdateRequest {
    return {
      passengerKey: passenger.passengerKey,
      assistanceServices: {
        isBlind: form.isBlind || undefined,
        isDeaf: form.isDeaf || undefined,
        wheelchairToFromGate: form.wheelchairToFromGate || undefined,
        wheelchairNoStairs: form.wheelchairNoStairs || undefined,
        wheelchairImmobile: form.wheelchairImmobile || undefined,
        manualWheelchair: form.manualWheelchair || undefined,
        nonSpillBatteryWheelchair: form.nonSpillBatteryWheelchair || undefined,
        wetCellBatteryWheelchair: form.wetCellBatteryWheelchair || undefined,
        cognitiveDisability: form.cognitiveDisability || undefined,
        hasOxygen: form.hasOxygen || undefined,
        hasServiceAnimal: form.hasServiceAnimal || undefined,
        lithiumBatteryWheelchair: form.lithiumBatteryWheelchair || undefined,
      } as { [key: string]: boolean | undefined },
    };
  }

  getPhoneNumberFromForm(
    form: PassengerForm | TripDetailsPassengerForm,
    phoneCodes: CountryPhoneCode[],
  ): string | undefined {
    if (!form.phoneCode || !form.phone) {
      return;
    }

    const phoneCodeNumber = phoneCodes.find(
      (code) => code.countryCode === form.phoneCode,
    )?.value;
    return createPhoneNumberFromParts(phoneCodeNumber, form.phone);
  }
}
